import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react'
import {LinksFAQParams, LinksFAQResponse} from 'pages/api/seo/links/faq'
import {getSeoApiUrl} from 'utils/apiUrls'

import {cacheFinalClientErrors} from '@vio/core/src/utils/network/cacheFinalClientErrors'

import type {HotelAverageRateParams} from 'pages/api/seo/hotels/average-rate'
import type {
  HotelDescriptionParams,
  HotelDescriptionResponse
} from 'pages/api/seo/hotels/description'
import type {HotelMetadataParams} from 'pages/api/seo/hotels/metadata'
import type {
  HotelPriceInsightsFAQParams,
  HotelPriceInsightsFAQResponse
} from 'pages/api/seo/hotels/price-insights-faqs'
import type {
  PopularLinksParams,
  PopularLinksResponse
} from 'pages/api/seo/interlinking/popular'
import type {
  RelatedLinksParams,
  RelatedLinksResponse
} from 'pages/api/seo/interlinking/related'
import type {
  TopHotelsParams,
  TopHotelsResponse
} from 'pages/api/seo/interlinking/top-hotels'
import type {AlternateLinksParams} from 'pages/api/seo/links/alternates'
import type {BreadcrumbParams} from 'pages/api/seo/links/breadcrumb'
import type {LinksMetadataParams} from 'pages/api/seo/links/metadata'
import type {AlternateLinksResponse} from 'seo/getAlternateLinks'
import type {BreadcrumbResponse} from 'seo/getBreadcrumb'
import type {HotelAverageRateResponse} from 'seo/getHotelAverageRate'
import type {HotelMetadataResponse} from 'seo/getHotelMetadata'
import type {LinksMetadataResponse} from 'seo/getLinksMetadata'

const SEO_API_URL = `${getSeoApiUrl()}/seo`

export const seoApi = createApi({
  reducerPath: 'seoApi',
  // seo-api answers "nothing for this page" with a 404 and rejects malformed
  // params with a 400. Neither is worth a second request from the browser.
  baseQuery: cacheFinalClientErrors(
    fetchBaseQuery({
      baseUrl: SEO_API_URL
    })
  ),
  endpoints: builder => ({
    getPopularLinks: builder.query<PopularLinksResponse, PopularLinksParams>({
      query: params => ({
        url: 'interlinking/popular',
        method: 'GET',
        params
      })
    }),
    getRelatedLinks: builder.query<RelatedLinksResponse, RelatedLinksParams>({
      query: params => ({
        url: 'interlinking/related',
        method: 'GET',
        params
      })
    }),
    getTopHotels: builder.query<TopHotelsResponse, TopHotelsParams>({
      query: params => ({
        url: 'interlinking/top-hotels',
        method: 'GET',
        params
      })
    }),
    getHotelMetadata: builder.query<HotelMetadataResponse, HotelMetadataParams>(
      {
        query: params => ({
          url: 'hotels/metadata',
          method: 'GET',
          params
        })
      }
    ),
    getHotelPriceInsightsFAQ: builder.query<
      HotelPriceInsightsFAQResponse,
      HotelPriceInsightsFAQParams
    >({
      query: params => ({
        url: 'hotels/price-insights-faqs',
        method: 'GET',
        params
      })
    }),
    getHotelAverageRate: builder.query<
      HotelAverageRateResponse,
      HotelAverageRateParams
    >({
      query: params => ({
        url: 'hotels/average-rate',
        method: 'GET',
        params
      })
    }),
    getHotelDescription: builder.query<
      HotelDescriptionResponse,
      HotelDescriptionParams
    >({
      query: params => ({
        url: 'hotels/description',
        method: 'GET',
        params
      })
    }),
    getLinksMetadata: builder.query<LinksMetadataResponse, LinksMetadataParams>(
      {
        query: params => ({
          url: 'links/metadata',
          method: 'GET',
          params
        })
      }
    ),
    getLinksFAQ: builder.query<LinksFAQResponse, LinksFAQParams>({
      query: params => ({
        url: 'links/faq',
        method: 'GET',
        params
      })
    }),
    getBreadcrumb: builder.query<BreadcrumbResponse, BreadcrumbParams>({
      query: params => ({
        url: 'links/breadcrumb',
        method: 'GET',
        params
      })
    }),
    getAlternateLinks: builder.query<
      AlternateLinksResponse,
      AlternateLinksParams
    >({
      query: params => ({
        url: 'links/alternates',
        method: 'GET',
        params
      })
    })
  })
})

export const {
  useGetPopularLinksQuery,
  useGetRelatedLinksQuery,
  useGetTopHotelsQuery,
  useGetHotelMetadataQuery,
  useGetHotelPriceInsightsFAQQuery,
  useGetHotelAverageRateQuery,
  useGetHotelDescriptionQuery,
  useGetLinksMetadataQuery,
  useGetLinksFAQQuery,
  useGetBreadcrumbQuery,
  useGetAlternateLinksQuery
} = seoApi
